import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatDuration,
  formatNumber,
  distanceValue,
  distanceUnitLabel,
  speedValue,
  speedUnitLabel,
} from '@boatnav/core';
import { Spline } from 'lucide-react';
import { listTracks, type TrackMeta } from '../../lib/db.js';
import { formatDateTime } from '../../lib/datetime.js';
import { useUnitsStore } from '../../app/unitsStore.js';
import { msg } from '../../lib/messages.js';
import { TrackThumb } from './TrackThumb.js';
import './tracks-list.css';

const PAGE = 20;

export function TracksListScreen(): JSX.Element {
  const units = useUnitsStore();
  const [tracks, setTracks] = useState<TrackMeta[]>([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadMore = useCallback(async (before?: number) => {
    setLoading(true);
    const page = await listTracks(PAGE, before);
    setTracks((prev) => (before == null ? page : [...prev, ...page]));
    setDone(page.length < PAGE);
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadMore();
  }, [loadMore]);

  return (
    <div className="tracks-list">
      <div className="screen-inner">
        <header className="tracks-list__head">
          <h1 className="tracks-list__title numeral">{msg.nav.tabTracks}</h1>
        </header>

        {tracks.length === 0 && !loading ? (
          <div className="empty-state">
            <Spline className="empty-state__icon" size={52} aria-hidden="true" />
            <div className="empty-state__title">{msg.tracks.emptyTitle}</div>
            <p className="empty-state__body">{msg.tracks.emptyBody}</p>
          </div>
        ) : (
          <ul className="tracks-list__items">
          {tracks.map((t) => (
            <li key={t.id}>
              <Link to={`/turer/${t.id}`} className="track-row">
                <TrackThumb path={t.thumb} />
                <div className="track-row__body">
                  <div className="track-row__name">{t.name}</div>
                  <div className="track-row__date">{formatDateTime(t.startedAt)}</div>
                  <div className="track-row__stats">
                    <span>
                      <i>{distanceUnitLabel[units.distance]}</i>{' '}
                      {formatNumber(distanceValue(t.distanceNm, units.distance), 1)}
                    </span>
                    <span>
                      <i>tid</i> {formatDuration(t.durationS / 60)}
                    </span>
                    <span>
                      <i>snitt</i> {formatNumber(speedValue(t.avgKn, units.speed), 1)}{' '}
                      {speedUnitLabel[units.speed]}
                    </span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

        {!done && tracks.length > 0 && (
          <button
            type="button"
            className="btn btn--ghost tracks-list__more"
            disabled={loading}
            onClick={() => loadMore(tracks[tracks.length - 1]?.startedAt)}
          >
            {msg.tracks.showMore}
          </button>
        )}
      </div>
    </div>
  );
}
